import { dbService, storageService } from '../firebaseInstance';
import { checkAdminPermission } from './adminAuth';

/**
 * 이미지 파일을 검증합니다.
 * @param {File} file - 검증할 이미지 파일
 * @returns {Object} 검증 결과
 */
export const validateImage = (file) => {
  const maxSize = 5 * 1024 * 1024; // 5MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  
  if (!file) {
    return { valid: false, error: '파일이 선택되지 않았습니다.' };
  }
  
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: '지원하지 않는 파일 형식입니다. (JPEG, PNG, WebP만 가능)' };
  }
  
  if (file.size > maxSize) {
    return { valid: false, error: '파일 크기가 너무 큽니다. (최대 5MB)' };
  }
  
  return { valid: true };
};

/**
 * 이미지를 리사이즈합니다.
 * @param {File} file - 원본 이미지 파일
 * @param {number} maxWidth - 최대 너비
 * @param {number} maxHeight - 최대 높이
 * @returns {Promise<Blob>} 리사이즈된 이미지
 */
export const resizeImage = async (file, maxWidth = 400, maxHeight = 400) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // 비율 유지하면서 리사이즈
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.8);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

/**
 * 특정 사용자에게 아바타를 업로드합니다.
 * @param {string} targetUserID - 대상 사용자 ID
 * @param {File} imageFile - 업로드할 이미지 파일
 * @param {string} adminUserID - 관리자 사용자 ID
 * @returns {Promise<Object>} 업로드 결과
 */
export const uploadAvatarForUser = async (targetUserID, imageFile, adminUserID) => {
  try {
    // 관리자 권한 확인
    const isAdmin = await checkAdminPermission(adminUserID);
    if (!isAdmin) {
      throw new Error('관리자 권한이 필요합니다.');
    }
    
    // 이미지 검증
    const validation = validateImage(imageFile);
    if (!validation.valid) {
      throw new Error(validation.error);
    }
    
    // 이미지 리사이즈
    const resizedImage = await resizeImage(imageFile);
    
    // Storage에 업로드
    const avatarRef = storageService.ref().child(`avatars/${targetUserID}.jpg`);
    const uploadTask = await avatarRef.put(resizedImage, {
      contentType: 'image/jpeg',
      cacheControl: 'public, max-age=31536000',
      customMetadata: {
        uploadedBy: adminUserID,
        uploadMethod: 'admin',
        uploadedAt: new Date().toISOString(),
        originalFileName: imageFile.name,
        fileSize: imageFile.size.toString()
      }
    });
    
    // 업로드 URL 가져오기
    const downloadURL = await avatarRef.getDownloadURL();
    
    // Firestore에 업로드 이력 기록
    await updateUserAvatarHistory(targetUserID, {
      uploadedAt: new Date(),
      uploadedBy: adminUserID,
      uploadMethod: 'admin',
      fileSize: imageFile.size,
      originalFileName: imageFile.name,
      downloadURL: downloadURL
    });
    
    console.log(`Avatar uploaded successfully for user ${targetUserID}`);
    
    return {
      success: true,
      downloadURL,
      message: '아바타가 성공적으로 업로드되었습니다.'
    };
    
  } catch (error) {
    console.error(`Avatar upload failed for user ${targetUserID}:`, error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * 여러 사용자에게 일괄 아바타 업로드를 수행합니다.
 * @param {Array} userImagePairs - 사용자-이미지 매핑 배열
 * @param {string} adminUserID - 관리자 사용자 ID
 * @returns {Promise<Object>} 일괄 업로드 결과
 */
export const bulkUploadAvatars = async (userImagePairs, adminUserID) => {
  try {
    // 관리자 권한 확인
    const isAdmin = await checkAdminPermission(adminUserID);
    if (!isAdmin) {
      throw new Error('관리자 권한이 필요합니다.');
    }
    
    const results = {
      total: userImagePairs.length,
      success: 0,
      failed: 0,
      details: []
    };
    
    for (const pair of userImagePairs) {
      const { userID, imageFile } = pair;
      
      try {
        const result = await uploadAvatarForUser(userID, imageFile, adminUserID);
        
        if (result.success) {
          results.success++;
        } else {
          results.failed++;
        }
        
        results.details.push({
          userID,
          success: result.success,
          error: result.error
        });
        
      } catch (error) {
        results.failed++;
        results.details.push({
          userID,
          success: false,
          error: error.message
        });
      }
    }
    
    return results;
    
  } catch (error) {
    console.error('Bulk upload failed:', error);
    return {
      total: 0,
      success: 0,
      failed: 0,
      details: [],
      error: error.message
    };
  }
};

/**
 * 사용자 아바타 업로드 이력을 업데이트합니다.
 * @param {string} userID - 사용자 ID
 * @param {Object} uploadInfo - 업로드 정보
 */
export const updateUserAvatarHistory = async (userID, uploadInfo) => {
  try {
    const userRef = dbService.collection("user").doc(userID);
    const userDoc = await userRef.get();
    
    if (!userDoc.exists) {
      console.warn(`User document not found for ${userID}`);
      return;
    }
    
    const userData = userDoc.data();
    const history = userData.avatarUploadHistory || [];
    
    // 새로운 업로드 정보 추가
    history.push(uploadInfo);
    
    // 최대 10개까지만 유지
    if (history.length > 10) {
      history.splice(0, history.length - 10);
    }
    
    await userRef.update({
      avatarUploadHistory: history,
      lastAvatarUpdate: new Date()
    });
    
  } catch (error) {
    console.error(`Update avatar history failed for ${userID}:`, error);
  }
};

/**
 * 특정 사용자의 아바타 업로드 이력을 가져옵니다.
 * @param {string} userID - 사용자 ID
 * @returns {Promise<Array>} 업로드 이력
 */
export const getUploadHistory = async (userID) => {
  try {
    const userDoc = await dbService.collection("user").doc(userID).get();
    if (!userDoc.exists) return [];
    
    const userData = userDoc.data();
    return userData.avatarUploadHistory || [];
  } catch (error) {
    console.error(`Get upload history failed for ${userID}:`, error);
    return [];
  }
}; 